import * as React from 'react'
// libs
import { change, getFormValues } from 'redux-form'
import { useDispatch } from 'react-redux'
import { Editor } from '@tiptap/react'
// store
import { store } from 'store/store'
// components
import { ReduxWysiwyg } from 'components/FormElements/ReduxForm'
import AddMetatagButton from './components/AddMetatagButton'
import SourceButton from './components/SourceButton'
// constants
import { Forms } from 'constants/index'
import { IRiskTemplate } from 'types/graphql'
// views
import { DocumentIcon, Wrapper } from './views'
// utils
import get from 'lodash/get'
import { required } from 'utils'

const getToolbarOptions = (isSource: boolean) => {
  if (isSource) {
    return { options: [] as [] }
  }

  return {
    options: ['inline', 'blockType', 'fontSize', 'fontFamily', 'list', 'textAlign'] as any
  }
}

// types
interface IIncomingProps {
  field: string
  template: string
  showEmailPreview: (template: any) => void
  parentRiskTemplates: IRiskTemplate[]
}

const EmailEditor: React.FC<IIncomingProps> = ({ field, template, showEmailPreview, parentRiskTemplates }) => {
  const [isSourceActive, setIsSourceActive] = React.useState(false)

  const editorRef = React.useRef<Editor | null>(null)

  const dispatch = useDispatch()

  const toggleSourceMode = (value: boolean) => {
    setIsSourceActive(value)

    if (value) {
      dispatch(change(Forms.SYSTEM_PROCESS_CREATE_FORM, `${field}.templateSnapshot`, template))
    } else {
      dispatch(change(Forms.SYSTEM_PROCESS_CREATE_FORM, `${field}.templateSnapshot`, undefined))
    }
  }

  const onSourceExit = () => {
    const formValues = getFormValues(Forms.SYSTEM_PROCESS_CREATE_FORM)(store.getState())

    dispatch(change(Forms.SYSTEM_PROCESS_CREATE_FORM, `${field}.template`, get(formValues, field)?.templateSnapshot))
  }

  const handleShowEmailPreview = () => {
    if (!isSourceActive) {
      showEmailPreview(template)
    }
  }

  const addMetatag = (metatag: string, closePopup: () => void) => {
    const editor = editorRef.current

    if (!editor) return

    editor.chain().focus().insertContent(` ${metatag} `).run()
    closePopup()
  }

  const setEditorReference = (editor: Editor | null) => {
    editorRef.current = editor
  }

  const toolbarOptions = getToolbarOptions(isSourceActive)

  return React.useMemo(
    () => (
      <Wrapper>
        <ReduxWysiwyg
          label='Email Body'
          validate={[required]}
          toolbar={toolbarOptions}
          name={`${field}.template`}
          editorRef={setEditorReference}
          toolbarCustomButtons={[
            <AddMetatagButton
              key='metatag'
              addMetatag={addMetatag}
              parentRiskTemplates={parentRiskTemplates}
              testId={`${field}.template-metatag`}
            />,
            <SourceButton
              key='source'
              editorRef={editorRef}
              onExit={onSourceExit}
              isActive={isSourceActive}
              toggleIsActive={toggleSourceMode}
            />
          ]}
        />
        <DocumentIcon onClick={handleShowEmailPreview} />
      </Wrapper>
    ),
    [field, isSourceActive, parentRiskTemplates?.length, showEmailPreview]
  )
}

export default EmailEditor
